import { supabase } from "./supabase";

/**
 * Logoene: selskapets og produksjonens.
 *
 * ========  TO STIER I SAMME BØTTE  ========
 *
 *   selskap/<selskap-id>/<fil>
 *   produksjon/<produksjon-id>/<fil>
 *
 * Førstesegmentet sier hvilken av dem det er, og policyene i migrasjon
 * 20261029140000 leser det. Selskapets logo kan byttes av den som setter
 * opp en av produksjonene i selskapet; produksjonens av den som setter opp
 * nettopp den.
 *
 * ========  EN LOGO ER IKKE ET DOKUMENT  ========
 *
 * Her brukes `upsert: true`, og filene kan fjernes. Det bryter ikke
 * regelen fra 20261029130000 om at leverte dokumenter aldri overskrives:
 * en logo er merkevare, ikke et levert dokument, og å bytte den er noe
 * noen har bedt om. Den gamle fila har ingen verdi når den nye er der.
 *
 * ========  FILNAVNET BÆRER TIDEN  ========
 *
 * `logo-<ms>.<ext>` og ikke `logo.png`. Nettleseren og Cloudflare
 * mellomlagrer en signert lenke, og med fast navn ville en byttet logo
 * blitt stående i gammel utgave til noen tømte cachen. Den forrige fila
 * fjernes etter at den nye er på plass — i den rekkefølgen, slik at en
 * feil midtveis etterlater en logo og ikke ingen.
 */

const BOETTE = "logoer";

/** Hva slags logo. Avgjør både stien og hvem som får bytte den. */
export type Slag = "selskap" | "produksjon";

export interface Svar {
  readonly ok: boolean;
  readonly feil?: string;
  /** Den nye stien, når det gikk bra. */
  readonly sti?: string;
}

const ENDELSER: Readonly<Record<string, string>> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/svg+xml": "svg",
  "image/webp": "webp",
};

/** 2 MB, som bøtta. Sies her også, slik at flata kan si det før den prøver. */
export const STOERSTE = 2 * 1024 * 1024;

export function sjekkFil(fil: File): string | null {
  if (ENDELSER[fil.type] === undefined) {
    return "Logoen må være PNG, JPG, SVG eller WebP.";
  }
  if (fil.size > STOERSTE) return "Logoen kan ikke være større enn 2 MB.";
  if (fil.size === 0) return "Fila er tom.";
  return null;
}

/**
 * Signert lenke til en logo, eller null.
 *
 * Bøtta er privat — en logo sier hvem som er i gang med hva, og det er
 * ikke noe vi legger åpent ut. Lenka varer en time, som de andre.
 */
export async function lenke(sti: string | null): Promise<string | null> {
  if (supabase === null || sti === null || sti === "") return null;
  const { data, error } = await supabase.storage
    .from(BOETTE).createSignedUrl(sti, 3600);
  if (error !== null || data === null) return null;
  return data.signedUrl;
}

export async function lastOpp(
  slag: Slag, eierId: string, fil: File, forrige: string | null,
): Promise<Svar> {
  if (supabase === null) return { ok: false, feil: "Ingen forbindelse." };

  const galt = sjekkFil(fil);
  if (galt !== null) return { ok: false, feil: galt };

  const endelse = ENDELSER[fil.type] ?? "png";
  const sti = `${slag}/${eierId}/logo-${Date.now()}.${endelse}`;

  /* Raa bytes, ikke Blob. Samme grunn som i dokumentopplastingen:
     `upload` med en Blob bygger en FormData med et navnloest felt, og paa
     iOS kommer den delen ikke med. Flata brukes ogsaa fra en iPad. */
  const { error: filfeil } = await supabase.storage
    .from(BOETTE)
    .upload(sti, await fil.arrayBuffer(), { contentType: fil.type, upsert: true });
  if (filfeil !== null) {
    return { ok: false, feil: `Fikk ikke lastet opp logoen: ${filfeil.message}` };
  }

  const { error } = await supabase
    .from(slag === "selskap" ? "company" : "production")
    .update({ logo_path: sti })
    .eq("id", eierId);
  if (error !== null) {
    return { ok: false, feil: `Logoen ble ikke lagret: ${error.message}` };
  }

  /* Først nå er den gamle overflødig. Feiler dette, har vi en fil for
     mye — ikke en logo for lite. */
  if (forrige !== null && forrige !== "" && forrige !== sti) {
    await supabase.storage.from(BOETTE).remove([forrige]);
  }
  return { ok: true, sti };
}

export async function fjern(
  slag: Slag, eierId: string, sti: string | null,
): Promise<Svar> {
  if (supabase === null) return { ok: false, feil: "Ingen forbindelse." };

  const { error } = await supabase
    .from(slag === "selskap" ? "company" : "production")
    .update({ logo_path: null })
    .eq("id", eierId);
  if (error !== null) return { ok: false, feil: error.message };

  if (sti !== null && sti !== "") {
    await supabase.storage.from(BOETTE).remove([sti]);
  }
  return { ok: true };
}

export interface Logoer {
  readonly selskapId: string | null;
  readonly selskapsnavn: string | null;
  readonly selskapLogo: string | null;
  readonly produksjonLogo: string | null;
}

/** Begge logoene, og selskapet produksjonen hører til. */
export async function hent(produksjonId: string): Promise<Logoer> {
  const tom: Logoer = {
    selskapId: null, selskapsnavn: null, selskapLogo: null, produksjonLogo: null,
  };
  if (supabase === null) return tom;

  const { data, error } = await supabase
    .from("production")
    .select("logo_path, company:company(id, name, logo_path)")
    .eq("id", produksjonId)
    .maybeSingle();
  if (error !== null || data === null) return tom;

  const rad = data as unknown as {
    logo_path: string | null;
    company: { id: string; name: string; logo_path: string | null } | null;
  };
  return {
    selskapId: rad.company?.id ?? null,
    selskapsnavn: rad.company?.name ?? null,
    selskapLogo: rad.company?.logo_path ?? null,
    produksjonLogo: rad.logo_path,
  };
}
